import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { useAppToast } from '../hooks/useAppToast';
import { AUTH_UNAUTHORIZED_EVENT } from '../lib/authEvents';

interface User {
  id: string;
  nome: string;
  email: string;
  role: string;
  tenantId?: string | null;
  ativo: boolean;
  ultimoLogin?: string | null;
  criadoEm: string;
}

/** Super-admin acessando como outro usuário: guarda o token dele para voltar */
export interface ImpersonationState { originalToken: string; email: string; nome: string; expiresAt: number }
const IMP_KEY = 'imp_session';
const readImp = (): ImpersonationState | null => {
  try { return JSON.parse(localStorage.getItem(IMP_KEY) || 'null'); } catch { return null; }
};

interface AuthContextType {
  impersonation: ImpersonationState | null;
  /** Troca a sessão pela do usuário; o token do super-admin fica guardado */
  startImpersonation: (token: string, user: User, expiresInMinutes: number) => Promise<void>;
  /** Encerra a personificação (revoga a sessão) e volta ao super-admin */
  stopImpersonation: () => Promise<void>;
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** Senha; se a conta tiver 2FA, devolve o token para a etapa do código */
  login: (email: string, senha: string, turnstileToken?: string | null) => Promise<LoginOutcome>;
  /** Abre a sessão com um token já emitido (2FA, passkey, login social) */
  startSession: (token: string, user?: User) => Promise<void>;
  logout: () => void;
  checkAuth: () => Promise<boolean>;
  setUser: (user: User | null) => void;
  setToken: (token: string) => void;
}

export type LoginOutcome =
  | { status: 'ok' }
  | { status: 'mfa'; mfaToken: string }
  | { status: 'error'; message: string };

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

const API_BASE_URL = '/api';

const VERIFY_ATTEMPTS = 3;
const VERIFY_RETRY_MS = 1500;

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('auth_token'));
  const [impersonation, setImpersonation] = useState<ImpersonationState | null>(readImp);
  const [isLoading, setIsLoading] = useState(true);
  const toast = useAppToast();

  const isAuthenticated = !!user && !!token;

  // Interceptar requisições para adicionar token
  const apiRequest = async (url: string, options: RequestInit = {}): Promise<Response> => {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    return fetch(`${API_BASE_URL}${url}`, {
      ...options,
      headers,
    });
  };

  const login = async (email: string, senha: string, turnstileToken?: string | null): Promise<LoginOutcome> => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, senha, ...(turnstileToken ? { turnstileToken } : {}) }),
      });

      const text = await response.text();
      let data: { message?: string; error?: string; mfaRequired?: boolean; mfaToken?: string; data?: { token: string; user: User } } = {};
      if (text) {
        try { data = JSON.parse(text); } catch { /* corpo não é JSON */ }
      }

      if (!response.ok) {
        return { status: 'error', message: data.message || data.error || 'Erro ao fazer login' };
      }
      if (data.mfaRequired && data.mfaToken) {
        return { status: 'mfa', mfaToken: data.mfaToken };
      }
      if (!data.data) {
        return { status: 'error', message: 'Resposta inválida do servidor ao fazer login' };
      }

      await startSession(data.data.token, data.data.user);
      return { status: 'ok' };
    } catch (error) {
      console.error('Erro no login:', error);
      return { status: 'error', message: 'Erro de conexão. Tente novamente.' };
    }
  };

  const startSession = async (newToken: string, userData?: User) => {
    localStorage.setItem('auth_token', newToken);
    setToken(newToken);
    if (userData) {
      setUser(userData);
      return;
    }
    // Login social: só temos o token; busca o usuário
    const res = await fetch(`${API_BASE_URL}/auth/verify`, { headers: { Authorization: `Bearer ${newToken}` } });
    if (res.ok) {
      const body = await res.json();
      setUser(body.data.user);
    } else {
      localStorage.removeItem('auth_token');
      setToken(null);
      throw new Error('Sessão inválida');
    }
  };

  const startImpersonation = async (newToken: string, target: User, expiresInMinutes: number) => {
    const original = localStorage.getItem('auth_token');
    if (!original) return;
    const state: ImpersonationState = { originalToken: original, email: target.email, nome: target.nome, expiresAt: Date.now() + expiresInMinutes * 60_000 };
    localStorage.setItem(IMP_KEY, JSON.stringify(state));
    setImpersonation(state);
    await startSession(newToken, target);
  };

  const stopImpersonation = async () => {
    const state = readImp();
    const current = localStorage.getItem('auth_token');
    if (current) await fetch('/api/platform/impersonate/stop', { method: 'POST', headers: { Authorization: `Bearer ${current}` } }).catch(() => undefined);
    localStorage.removeItem(IMP_KEY);
    setImpersonation(null);
    if (state) {
      await startSession(state.originalToken).catch(() => undefined);
      window.location.assign('/super-admin?aba=usuarios');
    }
  };

  const logout = () => {
    // Registra a saída na auditoria da organização (não espera a resposta)
    const current = localStorage.getItem('auth_token');
    if (current) {
      fetch('/api/auth/logout', { method: 'POST', headers: { Authorization: `Bearer ${current}` }, keepalive: true }).catch(() => undefined);
    }
    setUser(null);
    setToken(null);
    localStorage.removeItem('auth_token');
    localStorage.removeItem(IMP_KEY);
    setImpersonation(null);
    toast.success('Logout realizado com sucesso!');
  };

  const checkAuth = async (): Promise<boolean> => {
    if (!token) {
      setIsLoading(false);
      return false;
    }

    // Só 401/403 significam sessão inválida. Limite de requisições (429),
    // erro do servidor ou falha de rede são transitórios: tentamos de novo e,
    // se persistir, mantemos o token (a próxima carga verifica outra vez) em
    // vez de deslogar o usuário.
    try {
      for (let attempt = 0; attempt < VERIFY_ATTEMPTS; attempt++) {
        try {
          const response = await apiRequest('/auth/verify');

          if (response.status === 401 || response.status === 403) {
            logout();
            return false;
          }

          if (response.ok) {
            const data = await response.json();
            setUser(data.data.user);
            return true;
          }
        } catch (error) {
          console.error('Erro ao verificar autenticação:', error);
        }
        await new Promise(resolve => setTimeout(resolve, VERIFY_RETRY_MS * (attempt + 1)));
      }
      toast.error('Não foi possível verificar sua sessão', 'Tente recarregar a página em instantes.');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  // P3 review 2026-09-24: interceptação de 401 movida para
  // useAuthenticatedFetch. Cada requisição autenticada detecta 401,
  // dispara o evento `auth:unauthorized` e este listener faz logout.
  // Substitui o monkey-patch global de window.fetch (problemático com
  // hot-reload e libs de terceiros).
  const logoutRef = useRef<() => void>(() => {});
  useEffect(() => { logoutRef.current = logout; });

  useEffect(() => {
    const handler = () => logoutRef.current();
    window.addEventListener(AUTH_UNAUTHORIZED_EVENT, handler);
    return () => window.removeEventListener(AUTH_UNAUTHORIZED_EVENT, handler);
  }, []);

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated,
    isLoading,
    login,
    startSession,
    impersonation,
    startImpersonation,
    stopImpersonation,
    logout,
    checkAuth,
    setUser,
    setToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};